import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Avatar,
  Chip,
  Button,
  Breadcrumbs,
  Link,
  Divider,
  TextField,
  InputAdornment,
  Pagination,
  Stack,
  useTheme,
  useMediaQuery,
  Paper,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  FormControlLabel,
  Slider,
  Rating,
  Skeleton
} from '@mui/material';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../Component/LanguageProvider';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import ShareIcon from '@mui/icons-material/Share';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import CommentIcon from '@mui/icons-material/Comment';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import GavelIcon from '@mui/icons-material/Gavel';
import BusinessIcon from '@mui/icons-material/Business';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DescriptionIcon from '@mui/icons-material/Description';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

export const BlogsPage = () => {
  const { currentLang, direction } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  // دوال مساعدة للـ Avatar
  const getInitials = (name) => {
    const nameParts = name.split(' ');
    if (nameParts.length >= 2) {
      // خذ الحرف الأول من الاسم الأول فقط
      return nameParts[0][0].toUpperCase();
    }
    // لو اسم واحد، خذ أول حرف فقط
    return name.substring(0, 1).toUpperCase();
  };

  const maskSecondName = (name) => {
    const nameParts = name.split(' ');
    if (nameParts.length >= 2) {
      // الاسم الأول كامل + الحرف الأول من الاسم الثاني + نقاط
      const firstName = nameParts[0];
      const secondNameFirstLetter = nameParts[1][0];
      return `${firstName} ${secondNameFirstLetter}.`;
    }
    return name;
  };

  // لون موحد للـ Avatar - أزرق ثابت
  const AVATAR_COLOR = '#4361ee'; // أزرق ثابت

  // States
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [bookmarkedPosts, setBookmarkedPosts] = useState([]);
  const [likedPosts, setLikedPosts] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [dateRange, setDateRange] = useState([2020, 2024]);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  
  const postsPerPage = 6;

  // Scroll animation
  const { scrollYProgress } = useScroll();
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.95]);
  const opacity = useTransform(scrollYProgress, [0, 0.3], [1, 0.8]);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, []);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  const cardVariants = {
    hidden: { scale: 0.9, opacity: 0 },
    visible: (i) => ({
      scale: 1,
      opacity: 1,
      transition: {
        delay: i * 0.1,
        type: "spring",
        stiffness: 200,
        damping: 20
      }
    }),
    hover: {
      y: -10,
      boxShadow: '0 20px 40px rgba(102,126,234,0.3)',
      transition: { type: "spring", stiffness: 300 }
    }
  };

  const floatingAnimation = {
    y: [0, -20, 0],
    transition: {
      duration: 5,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  // Categories
  const categories = {
    en: [
      { id: 'all', name: 'All Posts', icon: DescriptionIcon, count: 24 },
      { id: 'labor-law', name: 'Labor Law', icon: GavelIcon, count: 8 },
      { id: 'business-setup', name: 'Business Setup', icon: BusinessIcon, count: 6 },
      { id: 'property-law', name: 'Property Law', icon: AccountBalanceIcon, count: 5 },
      { id: 'digital-nomads', name: 'Digital Nomads', icon: TrendingUpIcon, count: 3 },
      { id: 'news', name: 'Legal News', icon: NewReleasesIcon, count: 2 }
    ],
    ar: [
      { id: 'all', name: 'جميع المقالات', icon: DescriptionIcon, count: 24 },
      { id: 'labor-law', name: 'قانون العمل', icon: GavelIcon, count: 8 },
      { id: 'business-setup', name: 'تأسيس الأعمال', icon: BusinessIcon, count: 6 },
      { id: 'property-law', name: 'قانون العقارات', icon: AccountBalanceIcon, count: 5 },
      { id: 'digital-nomads', name: 'الرحالة الرقميون', icon: TrendingUpIcon, count: 3 },
      { id: 'news', name: 'أخبار قانونية', icon: NewReleasesIcon, count: 2 }
    ]
  };

  // Tags
  const tags = {
    en: ['UAE Law', 'UAE Courts', 'Legal Tips', 'Contracts', 'Employment', 'Visa', 'Free Zone', 'Mainland'],
    ar: ['قانون الإمارات', 'محاكم دبي', 'نصائح قانونية', 'عقود', 'عمالة', 'تأشيرات', 'مناطق حرة', 'بر رئيسي']
  };

  // Featured posts
  const featuredPosts = {
    en: [
      {
        id: 'featured-1',
        title: 'New UAE Labor Law: Complete Guide for Employers and Employees',
        excerpt: 'Everything you need to know about the latest amendments to the UAE Labor Law and how they affect your rights and obligations.',
        image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'Labor Law',
        date: '2024-03-15',
        readTime: '10 min',
        author: 'Ahmed Al Mansoori',
        views: 3456,
        likes: 234,
        comments: 45
      },
      {
        id: 'featured-2',
        title: 'Starting a Business in UAE Free Zones: 2024 Complete Guide',
        excerpt: 'Step-by-step guide to setting up your business in UAE Free Zones, including costs, requirements, and benefits.',
        image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'Business Setup',
        date: '2024-03-10',
        readTime: '12 min',
        author: 'Fatima Al Hashimi',
        views: 2789,
        likes: 189,
        comments: 32
      }
    ],
    ar: [
      {
        id: 'featured-1',
        title: 'قانون العمل الإماراتي الجديد: دليل شامل لأصحاب العمل والموظفين',
        excerpt: 'كل ما تحتاج معرفته عن التعديلات الجديدة في قانون العمل الإماراتي وكيف تؤثر على حقوقك والتزاماتك.',
        image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'قانون العمل',
        date: '2024-03-15',
        readTime: '10 دقائق',
        author: 'أحمد المنصوري',
        views: 3456,
        likes: 234,
        comments: 45
      },
      {
        id: 'featured-2',
        title: 'تأسيس شركة في المناطق الحرة بدبي: دليل 2024 الشامل',
        excerpt: 'دليل خطوة بخطوة لتأسيس شركتك في المناطق الحرة بدبي، بما في ذلك التكاليف والمتطلبات والمزايا.',
        image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'تأسيس الأعمال',
        date: '2024-03-10',
        readTime: '12 دقيقة',
        author: 'فاطمة الهاشمي',
        views: 2789,
        likes: 189,
        comments: 32
      }
    ]
  };

  // Blog posts data - extended version
  const blogPosts = {
    en: [
      {
        id: 1,
        title: 'Understanding UAE Labor Law: A Comprehensive Guide',
        excerpt: 'Learn about the key aspects of UAE labor law, including employee rights, contract types, and termination procedures.',
        content: `The UAE Labor Law is designed to protect both employers and employees, ensuring a fair and productive working environment. This comprehensive guide covers:

        1. Employment Contracts: Fixed-term and unlimited contracts explained
        2. Working Hours: Standard hours, overtime, and flexible arrangements
        3. Leave Entitlements: Annual, sick, and special leave provisions
        4. Termination: Legal procedures and end-of-service benefits
        5. Dispute Resolution: How to handle workplace conflicts
        
        Understanding these regulations is crucial for both employers and employees to maintain compliance and avoid legal issues.`,
        image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'Labor Law',
        categoryId: 'labor-law',
        date: '2024-03-15',
        readTime: '8 min',
        author: 'Ahmed Al Mansoori',
        views: 1234,
        likes: 89,
        comments: 23,
        tags: ['UAE Law', 'Employment', 'Contracts']
      },
      {
        id: 2,
        title: 'Starting a Business in UAE: Legal Requirements',
        excerpt: 'A step-by-step guide to understanding the legal framework for setting up a business in UAE and the UAE.',
        content: `UAE offers tremendous opportunities for entrepreneurs, but navigating the legal requirements can be complex. This guide walks you through:

        1. Business Structures: Mainland vs. Free Zone vs. Offshore
        2. Licensing Requirements: Commercial, industrial, and professional licenses
        3. Visa Processes: Investor and employment visas
        4. Local Sponsorship: When and how it's required
        5. Compliance: Ongoing legal obligations
        
        Whether you're a local entrepreneur or foreign investor, understanding these requirements is essential for success.`,
        image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'Business Setup',
        categoryId: 'business-setup',
        date: '2024-03-10',
        readTime: '10 min',
        author: 'Sara Al Hashimi',
        views: 2156,
        likes: 156,
        comments: 34,
        tags: ['UAE Courts', 'Free Zone', 'Mainland']
      },
      {
        id: 3,
        title: 'UAE Rental Disputes: What Tenants and Landlords Need to Know',
        excerpt: 'Essential information about resolving rental disputes in UAE through the Rental Dispute Settlement Center.',
        content: `Rental disputes are common in UAE's dynamic real estate market. This article explains:

        1. RERA and Ejari: Registration requirements and importance
        2. Rent Increases: Legal limits and notification periods
        3. Maintenance Responsibilities: Who pays for what
        4. Eviction Rules: Legal grounds and procedures
        5. Dispute Resolution: How to file a case at the Rental Dispute Center
        
        Both tenants and landlords benefit from understanding their rights and obligations under UAE's rental laws.`,
        image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'Property Law',
        categoryId: 'property-law',
        date: '2024-03-05',
        readTime: '7 min',
        author: 'Mohammed Al Falasi',
        views: 1876,
        likes: 134,
        comments: 28,
        tags: ['UAE Courts', 'Contracts', 'Legal Tips']
      },
      {
        id: 4,
        title: 'Legal Tips for Digital Nomads in the UAE',
        excerpt: 'Important legal considerations for remote workers and digital nomads planning to relocate to the UAE.',
        content: `The UAE has become a hotspot for digital nomads. Here's what you need to know legally:

        1. Visa Options: Remote work visas and freelance permits
        2. Tax Implications: Understanding your tax obligations
        3. Contract Considerations: Service agreements and NDAs
        4. Intellectual Property: Protecting your digital assets
        5. Banking and Finance: Setting up accounts and transferring money
        
        Stay compliant while enjoying the benefits of working from this dynamic country.`,
        image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'Digital Nomads',
        categoryId: 'digital-nomads',
        date: '2024-02-28',
        readTime: '6 min',
        author: 'Fatima Al Zaabi',
        views: 1432,
        likes: 98,
        comments: 17,
        tags: ['Visa', 'Legal Tips', 'Contracts']
      },
      {
        id: 5,
        title: 'UAE Corporate Tax: What Businesses Need to Know',
        excerpt: 'A comprehensive guide to the new UAE corporate tax regime and how it affects different business structures.',
        image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'Business Setup',
        categoryId: 'business-setup',
        date: '2024-02-20',
        readTime: '9 min',
        author: 'Khalid Al Nuaimi',
        views: 2341,
        likes: 187,
        comments: 42,
        tags: ['UAE Law', 'Free Zone', 'Mainland']
      },
      {
        id: 6,
        title: 'Employee Rights in UAE: Complete Guide 2024',
        excerpt: 'Everything employees need to know about their rights under UAE labor law, including leave, overtime, and termination.',
        image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'Labor Law',
        categoryId: 'labor-law',
        date: '2024-02-15',
        readTime: '8 min',
        author: 'Noura Al Shamsi',
        views: 1987,
        likes: 145,
        comments: 31,
        tags: ['Employment', 'Contracts', 'Legal Tips']
      },
      {
        id: 7,
        title: 'UAE Property Laws: Guide for Foreign Investors',
        excerpt: 'Understanding the legal framework for foreign property ownership in UAE and the UAE.',
        image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'Property Law',
        categoryId: 'property-law',
        date: '2024-02-10',
        readTime: '7 min',
        author: 'Mohammed Al Falasi',
        views: 1654,
        likes: 123,
        comments: 19,
        tags: ['UAE Courts', 'Contracts']
      },
      {
        id: 8,
        title: 'Free Zone vs Mainland: Choosing the Right Business Structure',
        excerpt: 'A detailed comparison between Free Zone and Mainland company setups in the UAE.',
        image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'Business Setup',
        categoryId: 'business-setup',
        date: '2024-02-05',
        readTime: '11 min',
        author: 'Sara Al Hashimi',
        views: 2876,
        likes: 234,
        comments: 56,
        tags: ['Free Zone', 'Mainland', 'UAE Law']
      },
      {
        id: 9,
        title: 'UAE Visa Guide for Investors and Entrepreneurs',
        excerpt: 'Everything you need to know about UAE visa options for investors, entrepreneurs, and business owners.',
        image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'Digital Nomads',
        categoryId: 'digital-nomads',
        date: '2024-01-28',
        readTime: '8 min',
        author: 'Fatima Al Zaabi',
        views: 2134,
        likes: 167,
        comments: 38,
        tags: ['Visa', 'Legal Tips']
      }
    ],
    ar: [
      {
        id: 1,
        title: 'دليل شامل لقانون العمل الإماراتي',
        excerpt: 'تعرف على الجوانب الرئيسية لقانون العمل الإماراتي، بما في ذلك حقوق الموظفين وأنواع العقود وإجراءات إنهاء الخدمة.',
        content: `صُمم قانون العمل الإماراتي لحماية كل من أصحاب العمل والموظفين، مما يضمن بيئة عمل عادلة ومنتجة. يغطي هذا الدليل الشامل:

        1. عقود العمل: شرح العقود محددة المدة وغير المحددة
        2. ساعات العمل: الساعات القياسية والعمل الإضافي والترتيبات المرنة
        3. استحقاقات الإجازات: الإجازات السنوية والمرضية والخاصة
        4. إنهاء الخدمة: الإجراءات القانونية ومكافآت نهاية الخدمة
        5. تسوية المنازعات: كيفية التعامل مع النزاعات في مكان العمل
        
        فهم هذه اللوائح أمر بالغ الأهمية لكل من أصحاب العمل والموظفين للحفاظ على الامتثال وتجنب المشكلات القانونية.`,
        image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'قانون العمل',
        categoryId: 'labor-law',
        date: '2024-03-15',
        readTime: '8 دقائق',
        author: 'أحمد المنصوري',
        views: 1234,
        likes: 89,
        comments: 23,
        tags: ['قانون الإمارات', 'عمالة', 'عقود']
      },
      {
        id: 2,
        title: 'تأسيس شركة في دبي: المتطلبات القانونية',
        excerpt: 'دليل خطوة بخطوة لفهم الإطار القانوني لتأسيس شركة في دبي والإمارات.',
        content: `تقدم دبي فرصاً هائلة لرواد الأعمال، لكن فهم المتطلبات القانونية قد يكون معقداً. يرشدك هذا الدليل خلال:

        1. هياكل الأعمال: المناطق الحرة vs. البر الرئيسي vs. المناطق الحرة
        2. متطلبات الترخيص: التراخيص التجارية والصناعية والمهنية
        3. إجراءات التأشيرات: تأشيرات المستثمرين وتأشيرات العمل
        4. الكفالة المحلية: متى وكيف تكون مطلوبة
        5. الامتثال: الالتزامات القانونية المستمرة
        
        سواء كنت رائد أعمال محلياً أو مستثمراً أجنبياً، فهم هذه المتطلبات ضروري للنجاح.`,
        image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'تأسيس الأعمال',
        categoryId: 'business-setup',
        date: '2024-03-10',
        readTime: '10 دقائق',
        author: 'سارة الهاشمي',
        views: 2156,
        likes: 156,
        comments: 34,
        tags: ['محاكم دبي', 'مناطق حرة', 'بر رئيسي']
      },
      {
        id: 3,
        title: 'منازعات الإيجار في دبي: ما يحتاج المستأجرون والمالكون معرفته',
        excerpt: 'معلومات أساسية حول حل المنازعات الإيجارية في دبي من خلال مركز فض المنازعات الإيجارية.',
        content: `المنازعات الإيجارية شائعة في سوق العقارات الديناميكي في دبي. يشرح هذا المقال:

        1. مؤسسة التنظيم العقاري وإيجاري: متطلبات التسجيل وأهميتها
        2. زيادات الإيجار: الحدود القانونية وفترات الإخطار
        3. مسؤوليات الصيانة: من يدفع ماذا
        4. قواعد الإخلاء: الأسباب والإجراءات القانونية
        5. تسوية المنازعات: كيفية رفع قضية في مركز المنازعات الإيجارية
        
        يستفيد كل من المستأجرين والمالكين من فهم حقوقهم والتزاماتهم بموجب قوانين الإيجار في دبي.`,
        image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'قانون العقارات',
        categoryId: 'property-law',
        date: '2024-03-05',
        readTime: '7 دقائق',
        author: 'محمد الفلاسي',
        views: 1876,
        likes: 134,
        comments: 28,
        tags: ['محاكم دبي', 'عقود', 'نصائح قانونية']
      },
      {
        id: 4,
        title: 'نصائح قانونية للرحالة الرقميين في الإمارات',
        excerpt: 'اعتبارات قانونية مهمة للعاملين عن بعد والرحالة الرقميين الذين يخططون للانتقال إلى الإمارات.',
        content: `أصبحت الإمارات وجهة مفضلة للرحالة الرقميين. إليك ما تحتاج معرفته قانونياً:

        1. خيارات التأشيرة: تأشيرات العمل عن بعد وتصاريح العمل الحر
        2. الآثار الضريبية: فهم التزاماتك الضريبية
        3. اعتبارات العقود: اتفاقيات الخدمة واتفاقيات عدم الإفصاح
        4. الملكية الفكرية: حماية أصولك الرقمية
        5. الخدمات المصرفية والمالية: فتح حسابات وتحويل الأموال
        
        حافظ على الامتثال القانوني أثناء الاستمتاع بفوائد العمل من هذه الدولة الديناميكية.`,
        image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'الرحالة الرقميون',
        categoryId: 'digital-nomads',
        date: '2024-02-28',
        readTime: '6 دقائق',
        author: 'فاطمة الزعابي',
        views: 1432,
        likes: 98,
        comments: 17,
        tags: ['تأشيرات', 'نصائح قانونية', 'عقود']
      },
      {
        id: 5,
        title: 'الضريبة على الشركات في الإمارات: ما تحتاج الشركات معرفته',
        excerpt: 'دليل شامل لنظام الضريبة الجديد على الشركات في الإمارات وكيف يؤثر على هياكل الأعمال المختلفة.',
        image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'تأسيس الأعمال',
        categoryId: 'business-setup',
        date: '2024-02-20',
        readTime: '9 دقائق',
        author: 'خالد النعيمي',
        views: 2341,
        likes: 187,
        comments: 42,
        tags: ['قانون الإمارات', 'مناطق حرة', 'بر رئيسي']
      },
      {
        id: 6,
        title: 'حقوق الموظفين في الإمارات: دليل 2024 الشامل',
        excerpt: 'كل ما يحتاج الموظفون معرفته عن حقوقهم بموجب قانون العمل الإماراتي، بما في ذلك الإجازات والعمل الإضافي وإنهاء الخدمة.',
        image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'قانون العمل',
        categoryId: 'labor-law',
        date: '2024-02-15',
        readTime: '8 دقائق',
        author: 'نورة الشامسي',
        views: 1987,
        likes: 145,
        comments: 31,
        tags: ['عمالة', 'عقود', 'نصائح قانونية']
      },
      {
        id: 7,
        title: 'قوانين العقارات في دبي: دليل للمستثمرين الأجانب',
        excerpt: 'فهم الإطار القانوني لتملك العقارات للأجانب في دبي والإمارات.',
        image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'قانون العقارات',
        categoryId: 'property-law',
        date: '2024-02-10',
        readTime: '7 دقائق',
        author: 'محمد الفلاسي',
        views: 1654,
        likes: 123,
        comments: 19,
        tags: ['محاكم دبي', 'عقود']
      },
      {
        id: 8,
        title: 'المناطق الحرة vs البر الرئيسي: اختيار هيكل الشركة المناسب',
        excerpt: 'مقارنة مفصلة بين تأسيس الشركات في المناطق الحرة والبر الرئيسي في الإمارات.',
        image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'تأسيس الأعمال',
        categoryId: 'business-setup',
        date: '2024-02-05',
        readTime: '11 دقيقة',
        author: 'سارة الهاشمي',
        views: 2876,
        likes: 234,
        comments: 56,
        tags: ['مناطق حرة', 'بر رئيسي', 'قانون الإمارات']
      },
      {
        id: 9,
        title: 'دليل تأشيرات الإمارات للمستثمرين ورواد الأعمال',
        excerpt: 'كل ما تحتاج معرفته عن خيارات التأشيرات الإماراتية للمستثمرين ورواد الأعمال وأصحاب الأعمال.',
        image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        category: 'الرحالة الرقميون',
        categoryId: 'digital-nomads',
        date: '2024-01-28',
        readTime: '8 دقائق',
        author: 'فاطمة الزعابي',
        views: 2134,
        likes: 167,
        comments: 38,
        tags: ['تأشيرات', 'نصائح قانونية']
      }
    ]
  };

  const currentCategories = categories[currentLang];
  const currentTags = tags[currentLang];
  const currentFeaturedPosts = featuredPosts[currentLang];
  const allPosts = blogPosts[currentLang];

  // Filter and sort posts
  const getFilteredPosts = () => {
    let filtered = [...allPosts];
    
    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(post => post.categoryId === selectedCategory);
    }
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(post =>
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Filter by tags
    if (selectedTags.length > 0) {
      filtered = filtered.filter(post =>
        post.tags.some(tag => selectedTags.includes(tag))
      );
    }
    
    // Sort
    switch(sortBy) {
      case 'newest':
        filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.date) - new Date(b.date));
        break;
      case 'popular':
        filtered.sort((a, b) => b.views - a.views);
        break;
      case 'most-liked':
        filtered.sort((a, b) => b.likes - a.likes);
        break;
      default:
        break;
    }
    
    return filtered;
  };

  const filteredPosts = getFilteredPosts();
  const pageCount = Math.ceil(filteredPosts.length / postsPerPage);
  const displayedPosts = filteredPosts.slice(
    (currentPage - 1) * postsPerPage,
    currentPage * postsPerPage
  );

  // Handle bookmark toggle
  const toggleBookmark = (postId) => {
    if (bookmarkedPosts.includes(postId)) {
      setBookmarkedPosts(bookmarkedPosts.filter(id => id !== postId));
    } else {
      setBookmarkedPosts([...bookmarkedPosts, postId]);
    }
  };

  // Handle like toggle
  const toggleLike = (postId) => {
    if (likedPosts.includes(postId)) {
      setLikedPosts(likedPosts.filter(id => id !== postId));
    } else {
      setLikedPosts([...likedPosts, postId]);
    }
  };

  // Handle tag selection
  const handleTagClick = (tag) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  // Reset filters
  const resetFilters = () => {
    setSelectedCategory('all');
    setSearchTerm('');
    setSelectedTags([]);
    setDateRange([2020, 2024]);
    setSortBy('newest');
    setCurrentPage(1);
  };

  return (
    <>
      {/* <Navbarsection /> */}
      
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '60vh', md: '80vh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          color: 'white',
          mb: { xs: 4, md: 8 },
          backgroundImage: 'url("https://images.unsplash.com/photo-1450101499163-c8848c66ca85?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80")',
          backgroundSize: 'cover',
          backgroundAttachment: { xs: 'scroll', md: 'fixed' },
          backgroundPosition: 'center',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(102,126,234,0.8) 100%)',
            zIndex: 1
          }
        }}
      >
        {/* Animated Background Elements */}
        <motion.div
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, 360, 0],
            opacity: [0.1, 0.2, 0.1]
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear"
          }}
          style={{
            position: 'absolute',
            top: '10%',
            right: '10%',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%)',
            zIndex: 2,
            pointerEvents: 'none'
          }}
        />

        <motion.div
          animate={floatingAnimation}
          style={{
            position: 'absolute',
            bottom: '15%',
            left: '5%',
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%)',
            zIndex: 2,
            pointerEvents: 'none'
          }}
        />

        {/* Content */}
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 3, textAlign: 'center' }}>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
          >
            {/* Breadcrumbs */}
            <Breadcrumbs 
              aria-label="breadcrumb"
              sx={{ 
                justifyContent: 'center', 
                display: 'flex',
                mb: 3,
                '& .MuiBreadcrumbs-separator': { color: 'rgba(255,255,255,0.5)' }
              }}
            >
              <Link
                component="button"
                onClick={() => navigate('/')}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'rgba(255,255,255,0.7)',
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  '&:hover': { color: 'white' }
                }}
              >
                <HomeIcon sx={{ fontSize: 16 }} />
                {currentLang === 'ar' ? 'الرئيسية' : 'Home'}
              </Link>
              <Typography sx={{ color: 'white', fontSize: '0.9rem' }}>
                {currentLang === 'ar' ? 'المدونة القانونية' : 'Legal Blog'}
              </Typography>
            </Breadcrumbs>

            {/* Main Title */}
            <motion.div
              animate={{ scale: [1, 1.02, 1] }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                  fontWeight: 800,
                  mb: 2,
                  fontSize: { xs: '2.5rem', md: '4rem' },
                  textShadow: '3px 3px 6px rgba(0,0,0,0.3)',
                  letterSpacing: '-0.02em'
                }}
              >
                {currentLang === 'ar' ? 'المدونة القانونية' : 'Legal Blog'}
              </Typography>
            </motion.div>

            {/* Description */}
            <Typography
              variant="h5"
              sx={{
                fontFamily: currentLang === 'ar' ? 'Cairo' : 'Inter',
                color: 'rgba(255,255,255,0.9)',
                mb: 5,
                fontSize: { xs: '1.1rem', md: '1.3rem' },
                maxWidth: '800px',
                mx: 'auto',
                textShadow: '1px 1px 2px rgba(0,0,0,0.2)',
                fontWeight: 400
              }}
            >
              {currentLang === 'ar'
                ? 'أحدث المقالات والأخبار القانونية في الإمارات'
                : 'Latest legal articles and news in the UAE'}
            </Typography>

            {/* Search Bar */}
            <Paper
              elevation={3}
              sx={{
                p: '2px 4px',
                display: 'flex',
                alignItems: 'center',
                width: { xs: '100%', md: '60%' },
                mx: 'auto',
                borderRadius: '50px',
                bgcolor: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)'
              }}
            >
              <InputAdornment position="start" sx={{ ml: 2 }}>
                <SearchIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />
              </InputAdornment>
              <TextField
                placeholder={currentLang === 'ar' ? 'ابحث في المقالات...' : 'Search articles...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    color: 'white',
                    '&::placeholder': {
                      color: 'rgba(255,255,255,0.7)',
                      opacity: 1
                    }
                  }
                }}
                sx={{ flex: 1 }}
              />
              <Button
                variant="contained"
                sx={{
                  borderRadius: '50px',
                  m: '4px',
                  px: 3,
                  background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #FF8E53 30%, #FE6B8B 90%)',
                  }
                }}
              >
                {currentLang === 'ar' ? 'بحث' : 'Search'}
              </Button>
            </Paper>
          </motion.div>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2, pb: 8 }}>
        <Grid container spacing={4}>
          {/* Sidebar - Filters */}
          <Grid item xs={12} md={3}>
            <motion.div variants={itemVariants} initial="hidden" animate="visible">
              {/* Mobile Filter Button */}
              {isMobile && (
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<FilterListIcon />}
                  onClick={() => setFilterDrawerOpen(true)}
                  sx={{ mb: 3, bgcolor: '#667eea' }}
                >
                  {currentLang === 'ar' ? 'تصفية البحث' : 'Filter Search'}
                </Button>
              )}

              {/* Desktop Sidebar */}
              {!isMobile && (
                <Paper sx={{ p: 3, borderRadius: '20px', position: 'sticky', top: 100 }}>
                  {/* Categories */}
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                    {currentLang === 'ar' ? 'التصنيفات' : 'Categories'}
                  </Typography>
                  <List>
                    {currentCategories.map((category) => {
                      const Icon = category.icon;
                      return (
                        <ListItem
                          button
                          key={category.id}
                          onClick={() => setSelectedCategory(category.id)}
                          selected={selectedCategory === category.id}
                          sx={{
                            borderRadius: '10px',
                            mb: 0.5,
                            '&.Mui-selected': {
                              bgcolor: '#667eea20',
                              color: '#667eea'
                            }
                          }}
                        >
                          <ListItemIcon>
                            <Icon sx={{ color: selectedCategory === category.id ? '#667eea' : '#666' }} />
                          </ListItemIcon>
                          <ListItemText 
                            primary={category.name}
                            secondary={`(${category.count})`}
                          />
                        </ListItem>
                      );
                    })}
                  </List>

                  <Divider sx={{ my: 3 }} />

                  {/* Tags */}
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                    {currentLang === 'ar' ? 'الوسوم' : 'Tags'}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {currentTags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        onClick={() => handleTagClick(tag)}
                        variant={selectedTags.includes(tag) ? 'filled' : 'outlined'}
                        sx={{
                          bgcolor: selectedTags.includes(tag) ? '#667eea' : 'transparent',
                          color: selectedTags.includes(tag) ? 'white' : '#666',
                          borderColor: '#667eea',
                          '&:hover': {
                            bgcolor: selectedTags.includes(tag) ? '#764ba2' : '#667eea20'
                          }
                        }}
                      />
                    ))}
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* Sort Options */}
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                    {currentLang === 'ar' ? 'ترتيب حسب' : 'Sort By'}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={sortBy === 'newest'}
                        onChange={() => setSortBy('newest')}
                        sx={{ color: '#667eea' }}
                      />
                    }
                    label={currentLang === 'ar' ? 'الأحدث' : 'Newest'}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={sortBy === 'oldest'}
                        onChange={() => setSortBy('oldest')}
                        sx={{ color: '#667eea' }}
                      />
                    }
                    label={currentLang === 'ar' ? 'الأقدم' : 'Oldest'}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={sortBy === 'popular'}
                        onChange={() => setSortBy('popular')}
                        sx={{ color: '#667eea' }}
                      />
                    }
                    label={currentLang === 'ar' ? 'الأكثر مشاهدة' : 'Most Popular'}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={sortBy === 'most-liked'}
                        onChange={() => setSortBy('most-liked')}
                        sx={{ color: '#667eea' }}
                      />
                    }
                    label={currentLang === 'ar' ? 'الأكثر إعجاباً' : 'Most Liked'}
                  />

                  <Divider sx={{ my: 3 }} />

                  {/* Reset Button */}
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={resetFilters}
                    sx={{ borderColor: '#667eea', color: '#667eea' }}
                  >
                    {currentLang === 'ar' ? 'إعادة تعيين' : 'Reset Filters'}
                  </Button>
                </Paper>
              )}
            </motion.div>
          </Grid>

          {/* Main Content - Posts */}
          <Grid item xs={12} md={9}>
            {/* Featured Posts */}
            {selectedCategory === 'all' && searchTerm === '' && selectedTags.length === 0 && (
              <Box sx={{ mb: 6 }}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
                  {currentLang === 'ar' ? 'مقالات مميزة' : 'Featured Posts'}
                </Typography>
                <Grid container spacing={3}>
                  {currentFeaturedPosts.map((post, index) => (
                    <Grid item xs={12} md={6} key={post.id}>
                      <motion.div
                        variants={cardVariants}
                        initial="hidden"
                        animate="visible"
                        custom={index}
                      >
                        <Card sx={{ borderRadius: '20px', overflow: 'hidden' }}>
                          <CardMedia
                            component="img"
                            height="200"
                            image={post.image}
                            alt={post.title}
                          />
                          <CardContent>
                            <Chip
                              label={post.category}
                              size="small"
                              sx={{ bgcolor: '#667eea', color: 'white', mb: 2 }}
                            />
                            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                              {post.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              {post.excerpt}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar
                                sx={{
                                  width: 30,
                                  height: 30,
                                  bgcolor: AVATAR_COLOR,
                                  color: 'white',
                                  fontWeight: 'bold',
                                  fontSize: '0.9rem'
                                }}
                              >
                                {getInitials(post.author)}
                              </Avatar>
                              <Typography variant="caption">{maskSecondName(post.author)}</Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Posts Grid */}
            <motion.div variants={containerVariants} initial="hidden" animate="visible">
              <Grid container spacing={3}>
                {displayedPosts.length > 0 ? (
                  displayedPosts.map((post, index) => (
                    <Grid item xs={12} sm={6} lg={4} key={post.id}>
                      <motion.div
                        variants={cardVariants}
                        custom={index}
                        whileHover="hover"
                        style={{ height: '100%' }}
                      >
                        <Card
                          sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            borderRadius: '20px',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            position: 'relative'
                          }}
                          onClick={() => navigate(`/blog/${post.id}`)}
                        >
                          {/* Bookmark Icon */}
                          <IconButton
                            sx={{
                              position: 'absolute',
                              top: 10,
                              right: 10,
                              bgcolor: 'rgba(255,255,255,0.9)',
                              zIndex: 1,
                              '&:hover': { bgcolor: 'white' }
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleBookmark(post.id);
                            }}
                          >
                            {bookmarkedPosts.includes(post.id) ? (
                              <BookmarkIcon sx={{ color: '#667eea' }} />
                            ) : (
                              <BookmarkBorderIcon />
                            )}
                          </IconButton>

                          <CardMedia
                            component="img"
                            height="180"
                            image={post.image}
                            alt={post.title}
                          />
                          
                          <CardContent sx={{ flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Chip
                                label={post.category}
                                size="small"
                                sx={{ bgcolor: '#667eea20', color: '#667eea', fontWeight: 500 }}
                              />
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <AccessTimeIcon sx={{ fontSize: 14, color: '#999' }} />
                                <Typography variant="caption" sx={{ color: '#999' }}>
                                  {post.readTime}
                                </Typography>
                              </Box>
                            </Box>

                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 'bold',
                                mb: 1,
                                fontSize: '1rem',
                                lineHeight: 1.4,
                                height: '3rem',
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical'
                              }}
                            >
                              {post.title}
                            </Typography>

                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                mb: 2,
                                height: '3rem',
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical'
                              }}
                            >
                              {post.excerpt}
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    bgcolor: AVATAR_COLOR,
                                    color: 'white',
                                    fontWeight: 'bold',
                                    fontSize: '0.8rem'
                                  }}
                                >
                                  {getInitials(post.author)}
                                </Avatar>
                                <Typography variant="caption">{maskSecondName(post.author)}</Typography>
                              </Box>
                              
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleLike(post.id);
                                  }}
                                >
                                  {likedPosts.includes(post.id) ? (
                                    <FavoriteIcon sx={{ color: '#e83e8c', fontSize: 18 }} />
                                  ) : (
                                    <FavoriteBorderIcon sx={{ fontSize: 18 }} />
                                  )}
                                </IconButton>
                                <Typography variant="caption">{post.likes}</Typography>
                                
                                <CommentIcon sx={{ fontSize: 16, color: '#999' }} />
                                <Typography variant="caption">{post.comments}</Typography>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 4, textAlign: 'center', borderRadius: '20px' }}>
                      <Typography variant="h6" color="text.secondary">
                        {currentLang === 'ar' ? 'لا توجد مقالات تطابق بحثك' : 'No articles match your search'}
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={resetFilters}
                        sx={{ mt: 2, borderColor: '#667eea', color: '#667eea' }}
                      >
                        {currentLang === 'ar' ? 'إعادة تعيين البحث' : 'Reset Search'}
                      </Button>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </motion.div>

            {/* Pagination */}
            {pageCount > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                <Pagination
                  count={pageCount}
                  page={currentPage}
                  onChange={(e, value) => setCurrentPage(value)}
                  color="primary"
                  size={isMobile ? 'medium' : 'large'}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: '#666',
                      '&.Mui-selected': {
                        bgcolor: '#667eea',
                        color: 'white',
                        '&:hover': {
                          bgcolor: '#764ba2'
                        }
                      }
                    }
                  }}
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </Container>

      {/* Mobile Filter Drawer */}
      <Drawer
        anchor={direction === 'rtl' ? 'right' : 'left'}
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        PaperProps={{
          sx: { width: '85%', maxWidth: 320, p: 2 }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {currentLang === 'ar' ? 'تصفية البحث' : 'Filter Search'}
          </Typography>
          <IconButton onClick={() => setFilterDrawerOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Categories */}
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
          {currentLang === 'ar' ? 'التصنيفات' : 'Categories'}
        </Typography>
        <List>
          {currentCategories.map((category) => {
            const Icon = category.icon;
            return (
              <ListItem
                button
                key={category.id}
                onClick={() => {
                  setSelectedCategory(category.id);
                  setFilterDrawerOpen(false);
                }}
                selected={selectedCategory === category.id}
                sx={{
                  borderRadius: '10px',
                  mb: 0.5,
                  '&.Mui-selected': {
                    bgcolor: '#667eea20',
                    color: '#667eea'
                  }
                }}
              >
                <ListItemIcon>
                  <Icon sx={{ color: selectedCategory === category.id ? '#667eea' : '#666' }} />
                </ListItemIcon>
                <ListItemText 
                  primary={category.name}
                  secondary={`(${category.count})`}
                />
              </ListItem>
            );
          })}
        </List>

        <Divider sx={{ my: 2 }} />

        {/* Tags */}
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
          {currentLang === 'ar' ? 'الوسوم' : 'Tags'}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {currentTags.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              onClick={() => handleTagClick(tag)}
              variant={selectedTags.includes(tag) ? 'filled' : 'outlined'}
              sx={{
                bgcolor: selectedTags.includes(tag) ? '#667eea' : 'transparent',
                color: selectedTags.includes(tag) ? 'white' : '#666',
                borderColor: '#667eea'
              }}
            />
          ))}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Sort Options */}
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
          {currentLang === 'ar' ? 'ترتيب حسب' : 'Sort By'}
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={sortBy === 'newest'}
              onChange={() => setSortBy('newest')}
              sx={{ color: '#667eea' }}
            />
          }
          label={currentLang === 'ar' ? 'الأحدث' : 'Newest'}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={sortBy === 'oldest'}
              onChange={() => setSortBy('oldest')}
              sx={{ color: '#667eea' }}
            />
          }
          label={currentLang === 'ar' ? 'الأقدم' : 'Oldest'}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={sortBy === 'popular'}
              onChange={() => setSortBy('popular')}
              sx={{ color: '#667eea' }}
            />
          }
          label={currentLang === 'ar' ? 'الأكثر مشاهدة' : 'Most Popular'}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={sortBy === 'most-liked'}
              onChange={() => setSortBy('most-liked')}
              sx={{ color: '#667eea' }}
            />
          }
          label={currentLang === 'ar' ? 'الأكثر إعجاباً' : 'Most Liked'}
        />

        <Divider sx={{ my: 2 }} />

        {/* Action Buttons */}
        <Button
          fullWidth
          variant="contained"
          onClick={resetFilters}
          sx={{ mb: 1, bgcolor: '#667eea' }}
        >
          {currentLang === 'ar' ? 'إعادة تعيين' : 'Reset Filters'}
        </Button>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => setFilterDrawerOpen(false)}
          sx={{ borderColor: '#667eea', color: '#667eea' }}
        >
          {currentLang === 'ar' ? 'إغلاق' : 'Close'}
        </Button>
      </Drawer>
    </>
  );
};

export default BlogsPage;